import React, { useCallback, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Check,
  Clock,
  Copy,
  Gift,
  Link2,
  Mail,
  Share2,
  UserPlus,
  Users,
  Wallet,
} from 'lucide-react';
import AppLoader from '../../components/modules/AppLoader';
import ModuleAlert from '../../components/modules/ModuleAlert';
import ModulePageHeader from '../../components/modules/ModulePageHeader';
import PartnerKitIcon from '../../components/common/PartnerKitIcon';
import { getThemeColor } from '../../config/appConfig';
import { useToast } from '../../contexts/ToastContext';
import apiService from '../../utils/apiService';
import { useFetchOnceByKey } from '../../hooks/useMountOnce';
import InviteEmailModal from './InviteEmailModal';
import UpdateCommissionModal from './UpdateCommissionModal';
import '../../styles/module-shared.css';
import './refer-a-friend.css';

const THEME = getThemeColor('primary');
const TABLE_PREVIEW = 10;

const pick = (obj, ...keys) => {
  for (const key of keys) {
    if (obj?.[key] != null && obj[key] !== '') return obj[key];
  }
  return undefined;
};

const toNum = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

const formatCurrency = (value) =>
  new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
  }).format(toNum(value));

const formatDate = (value) => {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
};

const statusClass = (status) => {
  const key = String(status || '').toLowerCase();
  if (key === 'joined' || key === 'completed' || key === 'active') return 'is-completed';
  if (key === 'pending') return 'is-pending';
  return `is-${key || 'pending'}`;
};

const normalizeReferrals = (list = []) =>
  (Array.isArray(list) ? list : []).map((row, index) => {
    const status = String(pick(row, 'status', 'Status') || 'Pending');
    const email = String(
      pick(row, 'affiliateSubPartnerEmail', 'AffiliateSubPartnerEmail', 'email') || ''
    );
    const name = String(pick(row, 'subPartnerName', 'SubPartnerName') || '').trim();
    const inviteId = Number(pick(row, 'affiliateReferSubPartnerId', 'AffiliateReferSubPartnerId') || 0);
    const subPartnerId = Number(pick(row, 'affiliateSubPartnerId', 'AffiliateSubPartnerId') || 0);
    const percentageRaw = pick(row, 'percentage', 'Percentage');
    const percentage = percentageRaw == null || percentageRaw === '' ? null : Number(percentageRaw);
    const joined =
      subPartnerId > 0 ||
      ['joined', 'completed', 'active'].includes(status.toLowerCase());
    return {
      id: (inviteId > 0 && inviteId) || (subPartnerId > 0 && `sub-${subPartnerId}`) || `ref-${index}`,
      inviteId,
      subPartnerId,
      name: name || email || '—',
      email,
      code: pick(row, 'subPartnerCode', 'SubPartnerCode') || '',
      date: pick(row, 'createdDateTime', 'CreatedDateTime') || null,
      status,
      statusLabel: status.toUpperCase(),
      percentage: Number.isFinite(percentage) ? percentage : null,
      canUpdateCommission: joined,
    };
  });

const emptySummary = {
  friendsReferred: 0,
  pending: 0,
  joined: 0,
  totalEarnings: 0,
  referralLink: '',
  referrals: [],
};

const parseReferSummary = (payload) => {
  const data = payload?.result ?? payload?.data ?? payload ?? {};
  const referrals = normalizeReferrals(pick(data, 'referrals', 'Referrals') || []);
  return {
    friendsReferred: toNum(pick(data, 'friendsReferred', 'FriendsReferred')),
    pending: toNum(pick(data, 'pending', 'Pending')),
    joined: toNum(pick(data, 'joined', 'Joined')),
    totalEarnings: toNum(pick(data, 'totalEarnings', 'TotalEarnings')),
    referralLink: String(pick(data, 'referralLink', 'ReferralLink') || '').trim(),
    referrals,
  };
};

const HOW_STEPS = [
  {
    icon: Share2,
    title: 'Share your link',
    body: 'Copy your unique partner link and send it to sub partners.',
  },
  {
    icon: UserPlus,
    title: 'Sub partner registers',
    body: 'They sign up, verify, and start trading with your referral.',
  },
  {
    icon: Gift,
    title: 'You both earn',
    body: 'Track referrals and rewards as sub partners become active.',
  },
];

const ReferAFriend = () => {
  const { showSuccess, showError } = useToast();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [partnerUrl, setPartnerUrl] = useState('');
  const [friendsReferred, setFriendsReferred] = useState(0);
  const [joined, setJoined] = useState(0);
  const [pending, setPending] = useState(0);
  const [totalEarnings, setTotalEarnings] = useState(0);
  const [referrals, setReferrals] = useState([]);
  const [copied, setCopied] = useState(false);
  const [emailModalOpen, setEmailModalOpen] = useState(false);
  const [sendingInvite, setSendingInvite] = useState(false);
  const [commissionTarget, setCommissionTarget] = useState(null);
  const [updatingCommission, setUpdatingCommission] = useState(false);

  const applySummary = useCallback((summary) => {
    setPartnerUrl(summary.referralLink);
    setFriendsReferred(summary.friendsReferred);
    setJoined(summary.joined);
    setPending(summary.pending);
    setTotalEarnings(summary.totalEarnings);
    setReferrals(summary.referrals);
  }, []);

  const fetchData = useCallback(
    async ({ cancelled }) => {
      setLoading(true);
      setError('');
      try {
        const res = await apiService.getReferSubPartners();
        if (cancelled()) return;
        if (!res.success) {
          setError(res.error || 'Failed to load referrals');
          applySummary(emptySummary);
        } else {
          applySummary(parseReferSummary(res.data));
        }
      } catch (err) {
        if (!cancelled()) setError(err.message || 'Failed to load referrals');
      } finally {
        if (!cancelled()) setLoading(false);
      }
    },
    [applySummary]
  );

  useFetchOnceByKey('refer-a-friend-v3', fetchData, true);

  const stats = useMemo(
    () => [
      {
        value: String(friendsReferred),
        label: 'Total Referrals',
        tone: 'slate',
        icon: Users,
      },
      {
        value: String(joined),
        label: 'Active Referrals',
        tone: 'green',
        icon: UserPlus,
      },
      {
        value: String(pending),
        label: 'Pending Referrals',
        tone: 'slate',
        icon: Clock,
      },
      {
        value: formatCurrency(totalEarnings),
        label: 'Total Earnings',
        tone: 'red',
        icon: Wallet,
      },
    ],
    [friendsReferred, joined, pending, totalEarnings]
  );

  const visibleReferrals = useMemo(
    () => referrals.slice(0, TABLE_PREVIEW),
    [referrals]
  );

  const handleCopy = async () => {
    if (!partnerUrl) {
      showError('Referral link not available yet');
      return;
    }
    try {
      await navigator.clipboard.writeText(partnerUrl);
      setCopied(true);
      showSuccess('Referral link copied');
      setTimeout(() => setCopied(false), 2000);
    } catch {
      showError('Failed to copy link');
    }
  };

  const formatPercent = (value) => {
    if (value == null || !Number.isFinite(Number(value))) return '—';
    const n = Number(value);
    return `${n % 1 === 0 ? n.toFixed(0) : n}%`;
  };

  const handleUpdateCommission = async (row, commissionPercentage) => {
    setUpdatingCommission(true);
    try {
      const res = await apiService.updateReferSubPartnerCommission({
        affiliateReferSubPartnerId: row.inviteId,
        affiliateSubPartnerId: row.subPartnerId,
        percentage: commissionPercentage,
      });
      if (!res.success) {
        showError(res.error || res.message || 'Failed to update commission');
        return false;
      }
      showSuccess(res.message || 'Commission updated');
      await fetchData({ cancelled: () => false });
      return true;
    } catch (err) {
      showError(err.message || 'Failed to update commission');
      return false;
    } finally {
      setUpdatingCommission(false);
    }
  };

  const handleSendInvite = async (friendEmail, commissionPercentage) => {
    setSendingInvite(true);
    try {
      const res = await apiService.sendSubPartnerInvite(friendEmail, commissionPercentage);
      if (!res.success) {
        showError(res.error || res.message || 'Failed to send invite');
        return false;
      }
      showSuccess(res.message || `Invite sent to ${friendEmail}`);
      await fetchData({ cancelled: () => false });
      return true;
    } catch (err) {
      showError(err.message || 'Failed to send invite');
      return false;
    } finally {
      setSendingInvite(false);
    }
  };

  if (loading) {
    return (
      <div className="page raf-page">
        <div className="page-inner">
          <div className="module-card">
            <AppLoader color={THEME} />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="page raf-page" style={{ '--raf-primary': THEME }}>
      <div className="page-inner">
        <ModulePageHeader
          icon={UserPlus}
          title="Refer a Sub Partner"
          subtitle="Invite sub partners and earn rewards when they join and trade"
          iconBg={`linear-gradient(135deg,${THEME},#a52a2a)`}
        />
        <ModuleAlert message={error} />

        <section className="raf-hero">
          <div className="raf-hero__copy">
            <span className="raf-hero__tag">Refer &amp; Earn</span>
            <h2>Invite sub partners. Earn together.</h2>
            <p>
              Share your unique link. When sub partners register, verify, and start trading, you earn
              commission from their activity.
            </p>
          </div>
          <div className="raf-hero__art" aria-hidden="true">
            <PartnerKitIcon
              name="referralGiftIllustration"
              size={150}
              alt=""
              className="object-contain"
              style={{ width: 150, height: 126 }}
            />
          </div>
        </section>

        <section className="raf-stats">
          {stats.map((item) => {
            const Icon = item.icon;
            return (
              <article key={item.label} className={`raf-stat is-${item.tone}`}>
                <div className="raf-stat__top">
                  <strong>{item.value}</strong>
                  <span className="raf-stat__icon">
                    <Icon className="w-4 h-4" />
                  </span>
                </div>
                <span>{item.label}</span>
              </article>
            );
          })}
        </section>

        <div className="raf-pills">
          <span>Unlimited referrals</span>
          <span>Track rewards in real time</span>
        </div>

        <div className="raf-grid">
          <section className="raf-card">
            <div className="raf-card__head">
              <Link2 className="w-4 h-4" />
              <div>
                <h3>Your referral link</h3>
                <p>Share this link so sub partners register with your referral.</p>
              </div>
            </div>
            <div className="raf-link-row">
              <input
                type="text"
                readOnly
                value={partnerUrl || 'Referral link will appear here'}
                className="raf-link-input"
                aria-label="Referral link"
              />
              <button type="button" className="raf-copy-btn" onClick={handleCopy}>
                {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                {copied ? 'Copied' : 'Copy link'}
              </button>
            </div>
            <button type="button" className="raf-email-btn" onClick={() => setEmailModalOpen(true)}>
              <Mail className="w-4 h-4" />
              Email
            </button>
          </section>

          <section className="raf-card">
            <div className="raf-card__head">
              <Users className="w-4 h-4" />
              <div>
                <h3>How it works</h3>
                <p>Three simple steps to start earning.</p>
              </div>
            </div>
            <ol className="raf-steps">
              {HOW_STEPS.map((step, index) => {
                const Icon = step.icon;
                return (
                  <li key={step.title}>
                    <span className="raf-steps__icon">
                      <Icon className="w-4 h-4" />
                    </span>
                    <div>
                      <strong>
                        {index + 1}. {step.title}
                      </strong>
                      <em>{step.body}</em>
                    </div>
                  </li>
                );
              })}
            </ol>
          </section>
        </div>

        <section className="raf-card raf-referrals">
          <div className="raf-card__head">
            <UserPlus className="w-4 h-4" />
            <div>
              <h3>Your referrals</h3>
              <p>Sub partners who joined using your link.</p>
            </div>
            <Link to="/clients/sub-partners" className="raf-view-all">
              View all →
            </Link>
          </div>

          {referrals.length === 0 ? (
            <div className="raf-empty">
              <Users className="raf-empty__icon" />
              <p>No referrals yet. Share your link to start inviting sub partners.</p>
            </div>
          ) : (
            <div className="raf-list">
              <div className="raf-list__head" aria-hidden="true">
                <span>Name / Email</span>
                <span>Code</span>
                <span>Commission</span>
                <span>Invited</span>
                <span>Status</span>
                <span>Action</span>
              </div>
              {visibleReferrals.map((row) => (
                <article key={row.id} className="raf-list__row">
                  <div className="raf-list__who">
                    <div className="raf-ref-name">{row.name}</div>
                    {row.email && row.name !== row.email ? (
                      <div className="raf-ref-email">{row.email}</div>
                    ) : null}
                  </div>
                  <div className="raf-list__field">
                    <span>Code</span>
                    <strong>{row.code || '—'}</strong>
                  </div>
                  <div className="raf-list__field">
                    <span>Commission</span>
                    <strong>{formatPercent(row.percentage)}</strong>
                  </div>
                  <div className="raf-list__field">
                    <span>Invited</span>
                    <strong>{formatDate(row.date)}</strong>
                  </div>
                  <div className="raf-list__field">
                    <span>Status</span>
                    <strong>
                      <span className={`raf-status ${statusClass(row.status)}`}>
                        {row.statusLabel}
                      </span>
                    </strong>
                  </div>
                  <div className="raf-list__field raf-list__field--action">
                    <span>Action</span>
                    {row.canUpdateCommission ? (
                      <button
                        type="button"
                        className="raf-update-btn"
                        onClick={() => setCommissionTarget(row)}
                      >
                        Update commission
                      </button>
                    ) : (
                      <strong>—</strong>
                    )}
                  </div>
                </article>
              ))}
            </div>
          )}
        </section>
      </div>

      <InviteEmailModal
        open={emailModalOpen}
        onClose={() => !sendingInvite && setEmailModalOpen(false)}
        sending={sendingInvite}
        onSend={handleSendInvite}
      />
      <UpdateCommissionModal
        open={Boolean(commissionTarget)}
        referral={commissionTarget}
        saving={updatingCommission}
        onClose={() => !updatingCommission && setCommissionTarget(null)}
        onSave={handleUpdateCommission}
      />
    </div>
  );
};

export default ReferAFriend;
